import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  ChevronsUpDown,
  ClipboardCheck,
  Code,
  FileText,
  KeyRound,
  LogOut,
  type LucideIcon,
  Menu as MenuIcon,
  Settings,
  ShieldAlert,
  Tags,
  Upload,
  X,
} from "lucide-react";
import { Dialog } from "radix-ui";
import { useState } from "react";
import { NavLink, Outlet } from "react-router";
import { api } from "@/lib/api";
import { useSession } from "@/lib/auth";
import { Logo } from "./Logo";
import { PasswordDialog } from "./PasswordDialog";
import { cn, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from "./ui";

interface NavEntry {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  /** Attention count shown beside the label (e.g. pending AI suggestions). */
  badge?: number;
}

function useNav(): { title: string; items: NavEntry[] }[] {
  const { me, hasRole } = useSession();
  const isAdmin = hasRole("admin");
  // Pending AI rule suggestions: the admin's notification, refreshed every minute.
  const suggestions = useQuery({
    queryKey: ["suggestion-count", me?.tenant?.id],
    queryFn: () => api<{ pending: number }>("/suggestions/count"),
    enabled: isAdmin,
    refetchInterval: 60_000,
  });
  const workspace: NavEntry[] = [
    { to: "/", label: "Analyses", icon: FileText, end: true },
    ...(hasRole("analyst") ? [{ to: "/analyses/new", label: "New analysis", icon: Upload }] : []),
    ...(hasRole("reviewer") ? [{ to: "/reviews", label: "Review queue", icon: ClipboardCheck }] : []),
    ...(hasRole("admin")
      ? [
          {
            to: "/categories",
            label: "Categorization",
            icon: Tags,
            badge: suggestions.data?.pending || undefined,
          },
          { to: "/settings", label: "Settings", icon: Settings },
          { to: "/api", label: "API", icon: Code },
        ]
      : []),
  ];
  const sections = [{ title: "Workspace", items: workspace }];
  if (me?.user.is_platform_admin) {
    sections.push({
      title: "Platform",
      items: [{ to: "/platform", label: "Organizations & users", icon: ShieldAlert }],
    });
  }
  return sections;
}

function initials(first?: string, last?: string, email?: string) {
  const s = `${first?.[0] ?? ""}${last?.[0] ?? ""}`.trim();
  return (s || email?.[0] || "?").toUpperCase();
}

function OrgSwitcher() {
  const { me, switchTenant } = useSession();
  if (!me) return null;
  const current = me.tenant;
  const canSwitch = me.tenants.length > 1;
  const box = (
    <>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-xs font-semibold text-white">
        {(current?.legal_name ?? "?").slice(0, 2).toUpperCase()}
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="block truncate text-sm font-medium text-slate-900">
          {current?.legal_name ?? "Choose organization"}
        </span>
        <span className="block truncate text-xs text-slate-500">
          {current?.roles.join(", ") ?? "platform"}
        </span>
      </span>
    </>
  );
  if (!canSwitch) return <div className="flex items-center gap-2.5 rounded-lg p-2">{box}</div>;
  return (
    <Menu>
      <MenuTrigger className="flex w-full items-center gap-2.5 rounded-lg p-2 ring-1 ring-slate-200 transition-colors hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-brand-600 data-[state=open]:bg-slate-50">
        {box}
        <ChevronsUpDown className="size-4 shrink-0 text-slate-400" />
      </MenuTrigger>
      <MenuContent className="w-(--radix-dropdown-menu-trigger-width)">
        <MenuLabel>Organizations</MenuLabel>
        {me.tenants.map((t) => (
          <MenuItem
            key={t.id}
            icon={Building2}
            checked={t.id === current?.id}
            onSelect={() => t.id !== current?.id && void switchTenant(t.id)}
          >
            {t.legal_name}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}

function UserMenu() {
  const { me, signOut } = useSession();
  const [changing, setChanging] = useState(false);
  if (!me) return null;
  const { first_name, last_name, email } = me.user;
  return (
    <>
      {(changing || me.user.must_change_password) && (
        <PasswordDialog forced={me.user.must_change_password} onClose={() => setChanging(false)} />
      )}
      <Menu>
        <MenuTrigger className="flex w-full items-center gap-2.5 rounded-lg p-2 transition-colors hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-brand-600 data-[state=open]:bg-slate-100">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800">
            {initials(first_name, last_name, email)}
          </span>
          <span className="min-w-0 flex-1 text-left">
            <span className="block truncate text-sm font-medium text-slate-900">
              {`${first_name} ${last_name}`.trim() || email}
            </span>
            <span className="block truncate text-xs text-slate-500">{email}</span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 text-slate-400" />
        </MenuTrigger>
        <MenuContent side="top" className="w-(--radix-dropdown-menu-trigger-width)">
          <MenuLabel>Signed in as {email}</MenuLabel>
          <MenuSeparator />
          <MenuItem icon={KeyRound} onSelect={() => setChanging(true)}>
            Change password
          </MenuItem>
          <MenuItem icon={LogOut} tone="danger" onSelect={signOut}>
            Sign out
          </MenuItem>
        </MenuContent>
      </Menu>
    </>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const sections = useNav();
  return (
    <div className="flex h-full flex-col gap-5 px-4 py-5">
      <div className="px-2">
        <Logo />
      </div>
      <OrgSwitcher />
      <nav className="flex flex-1 flex-col gap-5 overflow-y-auto">
        {sections.map((s) => (
          <div key={s.title}>
            <div className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              {s.title}
            </div>
            <ul className="space-y-0.5">
              {s.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        "group flex h-9 items-center gap-3 rounded-lg px-2.5 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-brand-50 text-brand-800"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          className={cn(
                            "size-[18px] shrink-0",
                            isActive ? "text-brand-700" : "text-slate-400 group-hover:text-slate-600",
                          )}
                        />
                        {item.label}
                        {item.badge ? (
                          <span
                            className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 tabular"
                            title={`${item.badge} AI suggestion${item.badge === 1 ? "" : "s"} waiting for approval`}
                          >
                            {item.badge}
                          </span>
                        ) : null}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="border-t border-slate-200 pt-4">
        <UserMenu />
      </div>
    </div>
  );
}

export function Layout() {
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-full">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-slate-200 bg-white lg:block">
        <Sidebar />
      </aside>

      {/* Mobile top bar + drawer */}
      <div className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur lg:hidden">
        <Dialog.Root open={open} onOpenChange={setOpen}>
          <Dialog.Trigger
            className="-ml-1 rounded-lg p-1.5 text-slate-600 hover:bg-slate-100"
            aria-label="Open menu"
          >
            <MenuIcon className="size-5" />
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-[1px]" />
            <Dialog.Content className="fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl">
              <Dialog.Title className="sr-only">Navigation</Dialog.Title>
              <Dialog.Close
                className="absolute top-5 right-4 rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                aria-label="Close menu"
              >
                <X className="size-5" />
              </Dialog.Close>
              <Sidebar onNavigate={() => setOpen(false)} />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
        <Logo />
      </div>

      <div className="lg:pl-64">
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10 lg:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
